/**
 * NotificationsSection: team failure-alert callback (TRA-1068).
 *
 * Where the production-monitoring notifier will deliver failure alerts
 * for this team (e.g. a Slack incoming webhook), plus an optional secret
 * it signs custom endpoint requests with.
 *
 * The secret is write-only (encrypted at rest, never returned), so the
 * form can't echo it back: an empty field keeps the stored secret, a
 * value replaces it, and removal is an explicit checkbox. Under staff
 * impersonation the form renders disabled; the API refuses the write.
 */

import { useState, type FormEvent } from 'react'
import { toast } from 'sonner'
import { Link } from 'react-router-dom'

import { getApiErrorDetail, getApiErrorStatus } from '@/api/client'
import type { NotificationSettings } from '@/api/generated/model'
import type { SettingsSectionProps } from '@/components/settings/sections'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Skeleton } from '@/components/ui/skeleton'
import { useTeam } from '@/contexts/TeamContext'
import { useNotificationSettings, useUpdateNotificationSettings } from '@/hooks/useTeamSettings'

import { SettingsFormFooter } from './SettingsFormFooter'
import { SettingsPageHeader } from './SettingsPageHeader'
import { SettingsBlock, SettingsSection } from './SettingsSection'

export function NotificationsSection({ section }: SettingsSectionProps): React.JSX.Element {
  const { impersonatingTeamId } = useTeam()
  const settingsQuery = useNotificationSettings()

  return (
    <div>
      <SettingsPageHeader section={section} />

      {settingsQuery.isLoading ? (
        <SettingsBlock>
          <Skeleton className="h-24 w-full" />
        </SettingsBlock>
      ) : settingsQuery.isError ? (
        <SettingsBlock>
          <p className="text-sm text-muted-foreground">
            {getApiErrorStatus(settingsQuery.error) === 404
              ? 'This team has no production traffic yet, so there is nothing to configure.'
              : (getApiErrorDetail(settingsQuery.error) ?? 'Failed to load notification settings.')}
          </p>
        </SettingsBlock>
      ) : settingsQuery.data ? (
        <NotificationsForm
          key={settingsQuery.data.team_id}
          settings={settingsQuery.data}
          readOnly={impersonatingTeamId !== null}
        />
      ) : null}
    </div>
  )
}

function NotificationsForm({
  settings,
  readOnly,
}: {
  settings: NotificationSettings
  readOnly: boolean
}): React.JSX.Element {
  const saveMutation = useUpdateNotificationSettings()
  const savedUrl = settings.callback_url ?? ''
  const [callbackUrl, setCallbackUrl] = useState(savedUrl)
  const [signingSecret, setSigningSecret] = useState('')
  const [clearSecret, setClearSecret] = useState(false)
  const [enabled, setEnabled] = useState(settings.enabled)
  // The API's answer to the last save. Any edit retires it: the next save
  // is a new question.
  const [saveError, setSaveError] = useState<string | null>(null)

  // Trimmed, matching what handleSubmit sends and the API stores: a URL typed
  // with spaces around it must read as clean once the trimmed value is saved.
  const dirty = enabled !== settings.enabled || callbackUrl.trim() !== savedUrl || signingSecret.trim() !== '' || clearSecret
  const urlError = validateCallbackUrl(callbackUrl, signingSecret)
  const canSubmit = !readOnly && !saveMutation.isPending && dirty && urlError === null

  const discard = (): void => {
    setEnabled(settings.enabled)
    setCallbackUrl(savedUrl)
    setSigningSecret('')
    setClearSecret(false)
    setSaveError(null)
  }

  const handleSubmit = (e: FormEvent): void => {
    e.preventDefault()
    if (!canSubmit) return
    setSaveError(null)
    saveMutation.mutate(
      {
        data: {
          callback_url: callbackUrl.trim() === '' ? null : callbackUrl.trim(),
          signing_secret: signingSecret.trim() === '' ? null : signingSecret.trim(),
          clear_signing_secret: clearSecret,
          enabled,
        },
      },
      {
        onSuccess: () => {
          setSigningSecret('')
          setClearSecret(false)
          toast.success('Notification settings saved')
        },
        onError: (error) => {
          const detail = getApiErrorDetail(error) ?? 'Failed to save notification settings'
          const status = getApiErrorStatus(error)
          if (status === 422 || status === 400) {
            setSaveError(detail)
          } else {
            toast.error(detail)
          }
        },
      },
    )
  }

  return (
    <form onSubmit={handleSubmit}>
      {/* One disabled fieldset turns every control off under impersonation. */}
      <fieldset disabled={readOnly} className="min-w-0">
        <SettingsSection
          title="Monitoring"
          description="Completed production chat trajectories are classified when their responses finish. Traffic spikes can delay alerts."
        >
          <div className="flex items-center gap-2">
            <Checkbox
              id="notification-enabled"
              checked={enabled}
              onCheckedChange={(checked) => {
                setEnabled(checked === true)
                setSaveError(null)
              }}
            />
            <Label htmlFor="notification-enabled">Send failure alerts</Label>
          </div>
          <p className="mt-2 text-xs text-muted-foreground">
            Monitoring requires full trace capture.
          </p>
          <Button asChild variant="outline" className="mt-3">
            <Link to="/settings/prompts">Edit classifier prompt</Link>
          </Button>
        </SettingsSection>
        <SettingsSection
          title="Delivery"
          description="Alerts are sent as HTTP POSTs. Use a Slack incoming webhook or your own endpoint."
        >
          <CallbackUrlField
            value={callbackUrl}
            error={urlError}
            onChange={(value) => {
              setCallbackUrl(value)
              setSaveError(null)
            }}
          />
        </SettingsSection>

        <SettingsSection
          title="Signing"
          description="Custom endpoints require a signing secret. Slack incoming webhooks do not need one."
        >
          <SigningSecretField
            isSet={settings.signing_secret_set}
            value={signingSecret}
            clear={clearSecret}
            onChange={(value) => {
              setSigningSecret(value)
              setSaveError(null)
            }}
            onClearChange={(next) => {
              setClearSecret(next)
              if (next) setSigningSecret('')
              setSaveError(null)
            }}
          />
        </SettingsSection>

        <SettingsFormFooter dirty={dirty} error={saveError}>
          <Button type="button" variant="outline" disabled={!dirty || saveMutation.isPending} onClick={discard}>
            Discard
          </Button>
          <Button type="submit" disabled={!canSubmit}>
            Save
          </Button>
        </SettingsFormFooter>
      </fieldset>
    </form>
  )
}

function CallbackUrlField({
  value,
  error,
  onChange,
}: {
  value: string
  error: string | null
  onChange: (value: string) => void
}): React.JSX.Element {
  return (
    <div className="max-w-xl space-y-2">
      <Label htmlFor="notification-callback-url">Callback URL</Label>
      <Input
        id="notification-callback-url"
        type="url"
        className="font-mono"
        value={value}
        onChange={(e) => {
          onChange(e.target.value)
        }}
        placeholder="https://hooks.slack.com/services/..."
        aria-invalid={error !== null}
        aria-describedby={
          error === null
            ? 'notification-callback-url-hint'
            : 'notification-callback-url-hint notification-callback-url-error'
        }
      />
      <p id="notification-callback-url-hint" className="text-xs text-muted-foreground">
        The URL must start with https:// and include a host. Leave it empty to turn alerts off.
      </p>
      {error !== null && (
        <p id="notification-callback-url-error" className="text-sm text-destructive" role="alert">
          {error}
        </p>
      )}
    </div>
  )
}

function SigningSecretField({
  isSet,
  value,
  clear,
  onChange,
  onClearChange,
}: {
  isSet: boolean
  value: string
  clear: boolean
  onChange: (value: string) => void
  onClearChange: (clear: boolean) => void
}): React.JSX.Element {
  return (
    <div className="max-w-xl space-y-4">
      <div className="space-y-2">
        <Label htmlFor="notification-signing-secret">Signing secret</Label>
        <Input
          id="notification-signing-secret"
          type="password"
          className="font-mono"
          value={value}
          autoComplete="off"
          disabled={clear}
          onChange={(e) => {
            onChange(e.target.value)
          }}
          placeholder={isSet ? 'Type a new secret to replace the current one' : 'Required for custom endpoints'}
          aria-describedby="notification-signing-secret-hint"
        />
        <p id="notification-signing-secret-hint" className="text-xs text-muted-foreground">
          {isSet
            ? 'A secret is set. Leave this empty to keep it. The secret is stored encrypted and never shown again.'
            : 'The secret is stored encrypted and never shown again.'}
        </p>
      </div>
      {isSet && (
        <div className="flex items-center gap-2">
          <Checkbox
            id="notification-clear-secret"
            checked={clear}
            onCheckedChange={(checked) => {
              onClearChange(checked === true)
            }}
          />
          <Label htmlFor="notification-clear-secret" className="text-sm font-normal">
            Remove the signing secret
          </Label>
        </div>
      )}
    </div>
  )
}

function validateCallbackUrl(callbackUrl: string, signingSecret: string): string | null {
  const url = callbackUrl.trim()
  if (url === '') {
    return signingSecret.trim() === ''
      ? null
      : 'A signing secret needs a callback URL. Set the URL too, or clear the secret.'
  }
  if (!/^https:\/\/./i.test(url)) {
    return 'The callback URL must start with https:// and include a host.'
  }
  return null
}
